/**
 * cashForecastLoader — chargement + calcul partagés du 12M Cash forecast.
 *
 * Centralise la séquence d'appels API (scénario + revenus/charges + projections
 * HR) et le calcul (computeCashModel → deriveCashSeries) utilisés à l'identique
 * par l'impression (CashForecastPrint) ET le Cockpit, afin que la projection de
 * trésorerie soit rigoureusement la même partout (source unique, pas de
 * recalcul divergent).
 *
 * Scénario retenu : celui dont `start_year === year`, sinon le premier du tenant.
 */
import {
  listScenarios, listRevenues, listVariableExpenses, listOneOffExpenses,
  listPayrollChanges, listBonuses, listSocialRates, listInsurances,
  listOpeningPositions,
} from '../../../api/treasury';
import { listIndirectCosts } from '../../../api/indirectCosts';
import { listVatRates } from '../../../api/tenants';
import { listHrContracts, peopleCostSynthesis, listPayrollMovements, listHrLppRates, listHrAnnualExtras } from '../../../api/hr';
import { getChargesSociales } from '../../../api/charges_sociales';
import { getBsView } from '../../../api/financials_bs';
import { computeCashModel } from './cashModel';
import { deriveCashSeries } from './cashSeries';
import { resolveOpeningPositions, openingPeriod } from './openingPositions';

// Agrège les avantages annuels sur un tenant×année.
// `varComp` porte désormais le total des compléments soumis aux charges sociales
// (extras_cs_total = Σ hr_extra_items, bonus/primes/jetons confondus) ; `bonus`
// reste à 0 (la distinction bonus vs variable n'existe plus, l'utilisateur
// désigne librement). Lissage cash ÷12 inchangé. `expenses` = indemnités/frais.
function aggregateAnnualExtras(rows) {
  const arr = rows?.data || rows || [];
  return arr.reduce((acc, r) => ({
    varComp: acc.varComp + Number(r.extras_cs_total || 0),
    bonus: acc.bonus,
    // People > Expenses : fixed + paid indemnities + other expenses
    expenses: acc.expenses + Number(r.fixed_indemnities || 0)
            + Number(r.paid_indemnities || 0) + Number(r.other_expenses || 0),
  }), { varComp: 0, bonus: 0, expenses: 0 });
}

/**
 * Charge toutes les données nécessaires au calcul du Cash forecast.
 * @returns {Promise<object>} data prêt pour computeForecastSeries, ou
 *   `{ scenario: null }` si aucun scénario de trésorerie pour ce tenant.
 */
export async function loadCashForecastData(tenantId, year, tenant = null, scenarioId = null) {
  if (!tenantId) return { scenario: null };
  const sList = await listScenarios(tenantId);
  const scenarios = sList.data || [];
  // `scenarioId` fourni (comparaison de courbes) → on cible ce scénario précis ;
  // sinon on retient celui dont start_year === year (défaut Cockpit / impression).
  const scenario = (scenarioId ? scenarios.find(s => s.id === scenarioId) : null)
    || scenarios.find(s => s.start_year === year) || scenarios[0] || null;
  if (!scenario) return { scenario: null };

  const sid = scenario.id;
  const startYear = scenario.start_year || year;

  const openingYear = openingPeriod(scenario).year;

  const [
    rRev, rVar, rOne, rPay, rBon, rSoc, rIns, rPos, rBs,
    rInd, rC, rM, rS, rLpp, rCs, rEx, rEx1, rVat,
  ] = await Promise.all([
    listRevenues(tenantId, sid), listVariableExpenses(tenantId, sid),
    listOneOffExpenses(tenantId, sid), listPayrollChanges(tenantId, sid),
    listBonuses(tenantId, sid), listSocialRates(tenantId, sid),
    listInsurances(tenantId, sid), listOpeningPositions(tenantId, startYear),
    getBsView(tenantId, openingYear).catch(() => ({ data: null })),
    listIndirectCosts(tenantId, startYear),
    listHrContracts(tenantId), listPayrollMovements(tenantId),
    peopleCostSynthesis(tenantId, startYear, 'all'), listHrLppRates(tenantId),
    getChargesSociales(tenantId, startYear).catch(() => ({ data: [] })),
    listHrAnnualExtras(tenantId, startYear).catch(() => ({ data: [] })),
    listHrAnnualExtras(tenantId, startYear + 1).catch(() => ({ data: [] })),
    listVatRates(tenantId).catch(() => ({ data: [] })),
  ]);

  const synth = rS?.data || rS;
  // Postes ouverts résolus en direct depuis le bilan d'arrêté.
  const resolved = resolveOpeningPositions({
    scenario,
    positions: rPos.data || [],
    bsViewByYear: rBs.data ? { [openingYear]: rBs.data } : {},
  });
  return {
    scenario,
    tenant,  // régime TVA porté par le tenant (moteur via composeEngineScenario)
    revenues: rRev.data || [], variables: rVar.data || [],
    oneOff: rOne.data || [], payrollChanges: rPay.data || [],
    bonuses: rBon.data || [], socialRates: rSoc.data || [],
    insurances: rIns.data || [],
    openingReceivables: resolved.receivableLines,
    openingPayables: resolved.payableLines,
    initialBalance: resolved.initialBalance,
    indirectCosts: rInd.data || [],
    hrContracts: rC.data || [], hrMovements: rM.data || [],
    hrCsRateEmpl: Number(synth?.cs_fallback_rate_empl) || 0,
    hrLppRates: rLpp.data || [], csLignes: rCs?.data || [],
    annualExtrasByYear: { [startYear]: aggregateAnnualExtras(rEx), [startYear + 1]: aggregateAnnualExtras(rEx1) },
    vatRates: rVat?.data || [],  // taux TVA par année (résolution year-aware dans le moteur)
  };
}

/**
 * Calcule la projection (moteur + séries) à partir des données chargées.
 * @returns {{ calc: object, s: object } | null}
 */
export function computeForecastSeries(data) {
  if (!data?.scenario) return null;
  try {
    const model = computeCashModel(data);
    if (!model.calc) return null;
    const s = deriveCashSeries({
      scenario: data.scenario, calc: model.calc,
      revenues: data.revenues, variables: data.variables,
      personnelProjection: model.personnelProjection,
      chargesSocialesMonthly: model.chargesSocialesMonthly,
      assurancesMonthly: model.assurancesMonthly,
      annualExtrasMonthly: model.annualExtrasMonthly,
    });
    return s ? { s, calc: model.calc } : null;
  } catch {
    return null;
  }
}
